"use client"
import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { graphql, readFragment, useSubscription } from "@opensea/graphql"
import { cloneDeep, set } from "es-toolkit/compat"
import { useState } from "react"

const UseLiveUpdateItemsBestOrdersSubscription = graphql(`
  subscription UseLiveUpdateItemsBestOrdersSubscription(
    $identifiers: [ItemIdentifierInput!]!
  ) {
    itemsByIdentifiers(identifiers: $identifiers) {
      id
      collection {
        id
        floorPrice {
          id
          pricePerItem {
            token {
              unit
            }
          }
        }
      }
      bestOffer {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      bestListing {
        pricePerItem {
          token {
            unit
          }
        }
      }
    }
  }
`)

export const useLiveUpdateItemsBestOrdersFragment = graphql(`
  fragment useLiveUpdateItemsBestOrders on Item {
    id
    tokenId
    contractAddress
    chain {
      identifier
    }
  }
`)

export function useLiveUpdateItemsBestOrders<T>(
  initialItems: (T & FragmentOf<typeof useLiveUpdateItemsBestOrdersFragment>)[],
) {
  const [items, setItems] = useState(initialItems)

  useSubscription(
    {
      query: UseLiveUpdateItemsBestOrdersSubscription,
      variables: {
        identifiers: initialItems
          .map(itemKey =>
            readFragment(useLiveUpdateItemsBestOrdersFragment, itemKey),
          )
          .map(item => ({
            tokenId: item.tokenId,
            contractAddress: item.contractAddress,
            chain: item.chain.identifier,
          })),
      },
    },
    {
      onResult: (
        updateData: ResultOf<typeof UseLiveUpdateItemsBestOrdersSubscription>,
      ) => {
        if (updateData?.itemsByIdentifiers) {
          setItems(prevItems => {
            return prevItems.map(prevItem => {
              const itemId = readFragment(
                useLiveUpdateItemsBestOrdersFragment,
                prevItem,
              ).id
              if (itemId !== updateData.itemsByIdentifiers.id) {
                return prevItem
              }
              const item = cloneDeep(prevItem)
              set(
                item,
                "collection.floorPrice.pricePerItem.token.unit",
                updateData.itemsByIdentifiers.collection?.floorPrice
                  ?.pricePerItem.token.unit,
              )
              set(
                item,
                "bestOffer.pricePerItem.token.unit",
                updateData.itemsByIdentifiers.bestOffer?.pricePerItem.token
                  .unit,
              )
              set(
                item,
                "bestListing.pricePerItem.token.unit",
                updateData.itemsByIdentifiers.bestListing?.pricePerItem.token
                  .unit,
              )
              return item
            })
          })
        }
      },
    },
  )

  return items
}
